import { Box, Checkbox, Flex, FlexProps, Text } from '@chakra-ui/react';
import { HeaderContext } from '@tanstack/react-table';
import { useRouter } from 'next/router';
import { ChangeEvent, MouseEvent, useCallback, useMemo } from 'react';
import { sortDirections } from '~/clientApi/agencies.model';
import { HeaderTextStyleProps } from '~/components/Table/Table.styles';
import { FEATURE_FLAGS } from '~/constants';
import { useListAgencies } from '~/hooks/api/useListAgencies';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { SortIcon } from '~/screens/MyAgency/components/Table/SortIcon';
import {
  useAdvancedFiltersStore,
  useCreatorsFiltersStore,
  useSavedListsStore,
} from '~/screens/MyAgency/store';
import { ICreator } from '~/types';

interface HeaderCellProps extends FlexProps {
  text?: string;
  justify?: FlexProps['justify'];
  header: HeaderContext<ICreator, any>;
  isFetching?: boolean;
  isSortable?: boolean;
  isFieldSorted?: (field: string) => false | sortDirections;
  creators?: ICreator[];
  textIcon?: React.ReactNode;
  trackId?: string;
}

export const HeaderCell: React.FC<HeaderCellProps> = ({
  text,
  justify,
  header,
  isSortable,
  isFetching,
  isFieldSorted,
  creators,
  trackId,
  textIcon,
  ...props
}) => {
  const router = useRouter();
  const slug = router.query['agency'] as string;
  const isShared = router.pathname === '/shared/[agency]';

  const { toggleAllCreators, creators: selectedCreators } =
    useSavedListsStore();
  const sortDirection = isFieldSorted && isFieldSorted(header?.column.id || '');
  const isSortByHeader =
    isFieldSorted && Boolean(isFieldSorted(header?.column.id));

  const savedListsFeatureFlag = useFeatureFlag(FEATURE_FLAGS.foamSavedLists);
  const foamAdvancedFilters = useFeatureFlag(FEATURE_FLAGS.foamAdvancedFilters);
  const isSavedListsEnabled = savedListsFeatureFlag === 'enabled';

  const currentAgency = useListAgencies(slug);
  // typescript v5 update issue
  const enabledControls = (currentAgency.data as any)?.results?.at(0)
    ?.enabledControls;

  const { appliedFilters } = useAdvancedFiltersStore();
  const { appliedFilters: appliedCreatorFilters } = useCreatorsFiltersStore();

  const isAudienceFiltersEnabled =
    isShared && enabledControls
      ? enabledControls.includes('audience-filters')
      : foamAdvancedFilters === 'enabled';

  const handleContainerClick = useCallback(
    (event: MouseEvent<HTMLDivElement, globalThis.MouseEvent>) => {
      if (isSavedListsEnabled) {
        event.stopPropagation();
      }
    },
    [isSavedListsEnabled],
  );

  const handleSelectAllCreators = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      event.stopPropagation();
      const isChecked = event.target.checked;

      toggleAllCreators(creators as ICreator[], isChecked);
    },
    [creators, toggleAllCreators],
  );

  const isChecked = useMemo(
    () =>
      creators
        ? selectedCreators.length === (creators as ICreator[]).length
        : false,
    [selectedCreators, creators],
  );
  return (
    <Flex
      justify={justify}
      alignItems="center"
      {...props}
      data-heap-track={`sortable-header-${header?.column.id}`}
      track-id={trackId}
    >
      {textIcon && <Box marginRight={2}>{textIcon}</Box>}
      {text &&
        text === 'Name' &&
        (appliedFilters.length > 0 ||
          appliedCreatorFilters.length > 0 ||
          selectedCreators.length > 0) &&
        isAudienceFiltersEnabled && (
          <Box marginRight={2} onClick={handleContainerClick}>
            <Checkbox
              onChange={handleSelectAllCreators}
              isChecked={isChecked}
            />
          </Box>
        )}
      {text && (
        <Text {...HeaderTextStyleProps} whiteSpace="nowrap">
          {text}
        </Text>
      )}
      <Box marginLeft={1}>
        {isSortable && (
          <SortIcon
            sortDirection={sortDirection || undefined}
            loading={isFetching && isSortByHeader}
          />
        )}
      </Box>
    </Flex>
  );
};
